/**
 * Template preview modal — click any template card → see it in action
 * (larger, auto-playing animation) → decide to "Use this template".
 *
 * Uses the same lightweight SVG approach as the grid card but scaled up and
 * with the `.is-playing` class permanently applied so the CSS keyframes
 * (defined in ads-creator.css) drive a looping ~4s preview of the template's
 * hero animations (text slide-in, CTA pulse, gradient shimmer).
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Sparkles, Loader2, Film } from "lucide-react";

const FORMAT_LABEL = {
  facebook_feed: "FB Feed", facebook_story: "FB Story",
  instagram_feed: "IG Feed", instagram_story: "IG Story",
  instagram_reel: "IG Reel",
};

export default function TemplatePreviewModal({
  template, onClose, onUse, starting, t = {},
}) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !starting) onClose();
    };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, starting]);

  if (!template) return null;
  const w = template.width || 1080;
  const h = template.height || 1080;
  const aspect = w / h;
  // Size the preview so that it fits nicely on desktop (max 520×740) and
  // on mobile (full-width minus gutters), preserving aspect ratio.
  const maxW = Math.min(520, window.innerWidth - 32);
  const maxH = Math.min(720, window.innerHeight - 220);
  let previewW, previewH;
  if (aspect > maxW / maxH) {
    previewW = maxW;
    previewH = maxW / aspect;
  } else {
    previewH = maxH;
    previewW = maxH * aspect;
  }

  const content = (
    <div
      ref={overlayRef}
      className="ads-tpl-modal-overlay"
      onClick={(e) => { if (e.target === overlayRef.current && !starting) onClose(); }}
      data-testid="ads-template-preview-modal"
    >
      <div className="ads-tpl-modal-card">
        <div className="ads-tpl-modal-head">
          <div>
            <div className="ads-tpl-modal-title">{template.title}</div>
            <div className="ads-tpl-modal-sub">
              {FORMAT_LABEL[template.format] || template.format}
              {template.animated && (
                <>
                  {" · "}
                  <Film size={10} style={{ verticalAlign: "-1px" }} /> VIDEO · {(template.duration_ms / 1000).toFixed(1)}s
                </>
              )}
              {template.premium ? " · PRO" : " · FREE"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!!starting}
            className="ads-tpl-modal-close"
            aria-label="Close"
            data-testid="ads-template-preview-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="ads-tpl-modal-stage">
          <div
            className={`ads-tpl-modal-preview-wrap ${template.animated ? "is-playing" : ""}`}
            style={{ width: previewW, height: previewH }}
          >
            <PreviewSvg canvas={template.canvas_json} w={w} h={h} animated={template.animated} />
          </div>
        </div>

        <div className="ads-tpl-modal-foot">
          <button
            type="button"
            className="ads-btn ads-btn-ghost"
            onClick={onClose}
            disabled={!!starting}
            data-testid="ads-template-preview-cancel"
          >
            {t.studio_preview_close || "Close"}
          </button>
          <button
            type="button"
            className="ads-btn ads-btn-primary"
            onClick={() => onUse(template)}
            disabled={!!starting}
            data-testid="ads-template-preview-use"
          >
            {starting ? (
              <><Loader2 size={14} className="animate-spin" /> {t.studio_preview_opening || "Opening editor…"}</>
            ) : (
              <><Sparkles size={14} /> {t.studio_preview_use || "Use this template"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

/**
 * SVG preview with staggered CSS classes on each object so the `.is-playing`
 * parent triggers a sequenced entrance animation (defined in ads-creator.css).
 */
function PreviewSvg({ canvas, w, h, animated }) {
  if (!canvas) return <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: "100%" }} />;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet"
         style={{ width: "100%", height: "100%", display: "block" }}>
      <rect x={0} y={0} width={w} height={h} fill={canvas.background || "#000"} />
      {(canvas.objects || []).map((o, i) => {
        const animClass = animated ? `ads-tpl-anim ads-tpl-anim-${i % 5}` : "";
        if (o.type === "rect") {
          return (
            <rect key={i} className={animClass}
              x={o.left || 0} y={o.top || 0}
              width={o.width || 0} height={o.height || 0}
              fill={o.fill || "#000"} opacity={o.opacity ?? 1}
              rx={o.rx || 0} ry={o.ry || 0}
            />
          );
        }
        if (o.type === "textbox") {
          return (
            <text key={i} className={animClass}
              x={(o.left || 0) + (o.textAlign === "center" ? (o.width || 0) / 2 : 0)}
              y={(o.top || 0) + (o.fontSize || 16)}
              fontFamily={o.fontFamily || "Inter"}
              fontWeight={o.fontWeight || 400}
              fontSize={o.fontSize || 20}
              fill={o.fill || "#fff"} opacity={o.opacity ?? 1}
              textAnchor={o.textAlign === "center" ? "middle" : "start"}
            >
              {(o.text || "").slice(0, 80)}
            </text>
          );
        }
        if (o.type === "image" && o.src) {
          return (
            <image key={i} className={animClass}
              href={o.src} xlinkHref={o.src}
              x={o.left || 0} y={o.top || 0}
              width={o.width || 0} height={o.height || 0}
              preserveAspectRatio="xMidYMid slice"
              opacity={o.opacity ?? 1}
            />
          );
        }
        if (o.type === "circle") {
          const cx = (o.left || 0) + (o.radius || 0);
          const cy = (o.top || 0) + (o.radius || 0);
          return (
            <circle key={i} className={animClass}
              cx={cx} cy={cy} r={o.radius || 0}
              fill={o.fill || "#fff"} opacity={o.opacity ?? 1} />
          );
        }
        return null;
      })}
    </svg>
  );
}
